import type { AgentAccess } from "@/components/agents/agent-access-card";
import type { CoderSettings } from "@/components/agents/coder-settings-card";
import type { TesterGate } from "@/components/agents/tester-gate-card";
import { AgentTabs } from "@/components/nav/agent-tabs";
import { Avatar } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/use-toast";
import { AGENT_AVATAR_BY_SLUG } from "@/lib/agent-avatars";
import { formatTokens, formatUsd } from "@/lib/agents";
import { ApiError, api } from "@/lib/api";
import { useRowMutation } from "@/lib/use-api-mutation";
import { Link, Outlet, useRouter } from "@tanstack/react-router";

/**
 * The agent shell: portrait, what it is, the four figures, the switches
 * — and the tabs under them. Same shape as a project's detail, because
 * an agent is read the same way: you arrive for one of a few separate
 * things (is it running, what did it cost, what did it do, how do I use
 * it), and a single scroll makes you hunt through all of them.
 *
 * The layout owns the fetch; each tab reads it back with
 * `useLoaderData({ from: "/_app/agents/$slug" })`, the way the project
 * tabs read their overview.
 */

export type AgentRun = {
  id: string;
  source: string;
  trigger: string;
  status: "running" | "completed" | "failed" | "interrupted";
  startedAt: string;
  finishedAt: string | null;
  result: Record<string, unknown> | null;
  error: string | null;
  model: string | null;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  costUsd: number;
  analyses: Array<{ id: string; sourceLabel: string | null }>;
  /** The one ticket the run was about, when there was exactly one. */
  workItem: { id: string; title: string; gitlabIssueId: string | null; globalId: number } | null;
};

export type TesterVerification = {
  id: string;
  workItemId: string;
  envName: string | null;
  mode: string;
  verdict: "passed" | "failed" | "not_testable" | "unclear";
  summary: string | null;
  outcomeAction: string | null;
  cause: string | null;
  evidence: { screenshots?: string[]; smokeScreenshot?: string } | null;
  createdAt: string;
  /** The run behind this verdict — one run per verification. */
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
  workItem: {
    title: string;
    gitlabIssueId: string | null;
    globalId: number;
    project: { slug: string } | null;
  } | null;
};

export type AgentChange = {
  id: string;
  kind: string;
  summary: string;
  revertable: boolean;
  revertedAt: string | null;
  revertError: string | null;
  createdAt: string;
  sessionId: string | null;
  project: { id: string; slug: string; name: string };
  workItem: { title: string; gitlabIssueId: string | null; globalId: number } | null;
};

export type AgentDetail = {
  agent: {
    id: string;
    slug: string;
    name: string;
    description: string | null;
    status: "active" | "paused";
    /** The model(s) this agent calls — each agent has its own, so the spend
     *  below only means something next to them. */
    models?: string[];
    /** `subscription`: tokens were reported but never billed per call, so
     *  the cost is what they would have cost at list price. */
    billing?: "metered" | "subscription";
  };
  /** Null for agents without model access of their own. */
  access: AgentAccess | null;
  /** Tunables of a coder agent (Metoděj); null for the others. */
  coderSettings?: CoderSettings | null;
  /** Prokop only: his login on the SSO gate in front of our environments. */
  gate?: TesterGate;
  runs: AgentRun[];
  daily: Array<{ day: string; tokens: number; costUsd: number }>;
  changes: AgentChange[];
  verifications?: TesterVerification[];
  stalls?: TesterStalls;
};

export type TesterStalls = {
  total: number;
  stalled: number;
  causes: Array<{
    cause: string;
    count: number;
    latest: { workItemId: string; title: string; globalId: number | null; at: string };
  }>;
};

/** Why a run ended without a verdict, and what usually fixes it. */

export const SOURCE_LABEL: Record<string, string> = {
  email: "Email",
  whatsapp: "WhatsApp",
  discord: "Discord",
  meeting: "Meeting",
  duplicate_scan: "Duplicate scan",
  verification: "Verification",
  review: "Review",
  log_triage: "Log triage",
  analyst_learning: "Learning",
  metodej_wi: "Work item",
  document: "Document",
};

/** Sources with a poller behind them — the ones "Run now" can trigger.
 *  Analyst-only; the Tester is triggered from a work item, not from here. */
const RUNNABLE_SOURCES = ["email", "whatsapp", "discord"] as const;

export async function loadAgentDetail(slug: string): Promise<AgentDetail> {
  return api.get<AgentDetail>(`/api/agents/${encodeURIComponent(slug)}`);
}

export function AgentLayout({ data, slug }: { data: AgentDetail; slug: string }) {
  const router = useRouter();
  const agent = data.agent;

  const runPass = useRowMutation(
    (source: string) => api.post(`/api/agents/${encodeURIComponent(slug)}/run`, { source }),
    {
      rowKey: (source) => source,
      onSuccess: (_res, source) => {
        toast({ title: `${SOURCE_LABEL[source] ?? source} pass started.` });
        void router.invalidate();
      },
      errorMessage: (err) =>
        (err instanceof ApiError && err.detail && typeof err.detail === "object"
          ? (err.detail as { error?: string }).error
          : undefined) ?? "Couldn't start the run.",
      onError: (msg) => toast({ title: msg, variant: "destructive" }),
    },
  );

  async function toggleStatus() {
    const status = agent.status === "active" ? "paused" : "active";
    await api.patch(`/api/agents/${encodeURIComponent(slug)}`, { status }).catch(() => undefined);
    void router.invalidate();
  }

  const today = data.daily.at(-1);
  const month = data.daily.reduce(
    (sum, day) => ({ tokens: sum.tokens + day.tokens, costUsd: sum.costUsd + day.costUsd }),
    { tokens: 0, costUsd: 0 },
  );

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <Avatar src={AGENT_AVATAR_BY_SLUG[slug]} name={agent.name} size={72} />
          <div>
            <p className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
              Agent
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="font-bold font-heading text-2xl leading-tight">{agent.name}</h1>
              <Badge
                variant={agent.status === "active" ? "default" : "secondary"}
                data-testid="agent-status"
              >
                {agent.status === "active" ? "Active" : "Paused"}
              </Badge>
            </div>
            {agent.description && (
              <p className="mt-1 max-w-3xl text-muted-foreground text-sm">{agent.description}</p>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {agent.status === "active" &&
            slug === "analyst" &&
            RUNNABLE_SOURCES.map((source) => (
              <Button
                key={source}
                type="button"
                variant="outline"
                size="sm"
                disabled={runPass.busy}
                onClick={() => runPass.run(source)}
                data-testid={`run-now-${source}`}
              >
                {runPass.isRunning(source) ? "Starting…" : `Run ${SOURCE_LABEL[source]}`}
              </Button>
            ))}
          <Button
            type="button"
            variant={agent.status === "active" ? "outline" : "default"}
            size="sm"
            onClick={toggleStatus}
            data-testid="agent-toggle"
          >
            {agent.status === "active" ? "Pause" : "Resume"}
          </Button>
          <Button asChild variant="ghost" size="sm">
            <Link to="/agents">Back</Link>
          </Button>
        </div>
      </div>

      <div className="mb-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Figure
          label="Today"
          value={`${formatTokens(today?.tokens ?? 0)} tok`}
          hint={formatUsd(today?.costUsd ?? 0)}
          testId="agent-today"
        />
        <Figure
          label="Last 30 days"
          value={`${formatTokens(month.tokens)} tok`}
          hint={
            agent.billing === "subscription"
              ? `${formatUsd(month.costUsd)} — estimate at list price, not a bill`
              : formatUsd(month.costUsd)
          }
          testId="agent-month"
        />
        <Figure
          label="Runs on"
          value={agent.billing === "subscription" ? "Subscription" : "Gateway key"}
          hint={data.access ? `engine: ${data.access.engine}` : "no model access of its own"}
          testId="agent-billing"
        />
        <Figure
          label="Model"
          value={agent.models?.length ? (agent.models[0] ?? "—") : "—"}
          hint={agent.models && agent.models.length > 1 ? agent.models.slice(1).join(" · ") : null}
          testId="agent-models"
        />
      </div>

      <AgentTabs
        slug={slug}
        hasVerifications={Boolean(data.verifications)}
        changeCount={data.changes?.length ?? 0}
        runCount={data.runs.length}
      />
      <Outlet />
    </div>
  );
}

function Figure({
  label,
  value,
  hint,
  testId,
}: {
  label: string;
  value: string;
  hint?: string | null;
  testId: string;
}) {
  return (
    <div className="rounded-lg border bg-card p-4" data-testid={testId}>
      <div className="text-muted-foreground text-xs">{label}</div>
      <div className="mt-1 font-semibold text-lg leading-none">{value}</div>
      {hint && <div className="mt-1.5 text-muted-foreground text-xs">{hint}</div>}
    </div>
  );
}

export type { AgentAccess, TesterGate };
